import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useReactiveVar } from '@apollo/client/react'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import {
  addDays,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  formatISO,
  intlFormat,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import PropTypes from 'prop-types'
import { Analytics } from 'shared-components'

import { store, timezoneAbbr } from '../../cache'
import i18next from '../../i18n'
import { getAvailability } from '../../utils/getAvailability'
import { newestToOldest } from '../../utils/newestToOldest'
import { Day } from '../Day'
import { FirstAvailable } from '../FirstAvailable'
import { NextMonth } from './NextMonth'
import { PreviousMonth } from './PreviousMonth'

import './style.css'

export const Calendar = ({
  onDaySelected,
  selectedDay,
  currentDate,
  onChangeDate,
  reservations,
}) => {
  const today = new Date()
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const weeks = eachWeekOfInterval({ start: monthStart, end: monthEnd })
  const { t } = useTranslation()
  const langCode = i18next.language

  const currentMonthYear = intlFormat(
    currentDate,
    {
      year: 'numeric',
      month: 'long',
    },
    { locale: langCode }
  )

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 })
  const weekdays = Array.from({ length: 7 }).map((_, i) => {
    return intlFormat(
      addDays(weekStart, i),
      { weekday: 'short' },
      { locale: langCode }
    )
  })

  useEffect(() => {
    if (reservations.length > 0) {
      timezoneAbbr(reservations[0].timezoneAbbr)
    }
  }, [reservations])

  const { locationId, partnerId } = useReactiveVar(store)
  const goToToday = () => {
    Analytics.todaySelected({
      locationId,
      partnerId,
      dayOfWeek: format(today, 'EEEE').toUpperCase(),
    })
    const todayAvailability = getAvailability(today, reservations)
    if (todayAvailability.times.length > 0) {
      onChangeDate(today)
      onDaySelected(today, todayAvailability)
    } else {
      onChangeDate(today)
      onDaySelected(null)
    }
  }

  return (
    <div className="calendar">
      <div className="calendar-header">
        <Typography
          className="month-year"
          sx={{
            color: 'var(--intelibly-branding-neutral-900)',
            fontWeight: 500,
          }}
          variant="h6"
        >
          {currentMonthYear}
        </Typography>
        <div className="arrow-button-container">
          <PreviousMonth
            currentDate={currentDate}
            today={today}
            onChangeDate={onChangeDate}
            monthStart={new Date(monthStart)}
          />
          <NextMonth currentDate={currentDate} onChangeDate={onChangeDate} />
        </div>
      </div>
      <Divider />
      <Button
        aria-label="today"
        color="primary"
        fullWidth
        onClick={() => goToToday()}
        startIcon={<TodayRoundedIcon />}
        variant="text"
      >
        {t('Today')}
      </Button>
      <div className="date-picker">
        <ul className="weekdays">
          {weekdays.map(day => (
            <li key={day}>
              <Typography variant="body2" className="week-day" key={day}>
                {day}
              </Typography>
            </li>
          ))}
        </ul>
        <ol className="days-grid">
          {weeks.map(startOfWeek =>
            eachDayOfInterval({
              start: startOfWeek,
              end: endOfWeek(startOfWeek),
            }).map(day => (
              <li key={formatISO(day, { representation: 'date' })}>
                <Day
                  day={new Date(day)}
                  availability={getAvailability(day, reservations)}
                  onSelectDay={onDaySelected}
                  selectedDay={selectedDay}
                />
              </li>
            ))
          )}
        </ol>
      </div>
    </div>
  )
}

Calendar.propTypes = {
  onDaySelected: PropTypes.func,
  selectedDay: PropTypes.instanceOf(Date),
  currentDate: PropTypes.instanceOf(Date).isRequired,
  onChangeDate: PropTypes.func.isRequired,
  reservations: PropTypes.array.isRequired,
}
